import { isMobileViewport } from '@/lib/utils/isMobileViewport';
import { API_BASE_URL } from '@/utils/api';
import logger from '@/utils/logger';

export const initiateSSOLogin = async (
  auth0LoginWithRedirect,
  appBrandName,
  { ssoConnectionName: preloadedConnectionName } = {},
) => {
  if (!appBrandName || typeof appBrandName !== 'string' || appBrandName.trim() === '') {
    const errorMessage = `A valid application brandName must be provided for SSO. Received: ${appBrandName}`;
    logger.error(new Error(errorMessage), { context: 'AuthUtils' });
    throw new Error(errorMessage);
  }

  // Special-case: For Yubico, use Auth0 Universal Login with DB connection and request MFA
  // so users can enroll and use their YubiKey (WebAuthn) via hosted pages.
  try {
    const isYubico = appBrandName.trim().toLowerCase() === 'yubico';
    if (isYubico) {
      let promptLogin = false;
      try {
        if (typeof window !== 'undefined') {
          promptLogin = window.sessionStorage.getItem('force_interactive_login') === '1';
          if (promptLogin) window.sessionStorage.removeItem('force_interactive_login');

          // Remove organization parameter from URL if present (Auth0 SDK might read it from URL)
          const url = new URL(window.location.href);
          if (url.searchParams.has('organization')) {
            url.searchParams.delete('organization');
            window.history.replaceState({}, '', url.toString());
            logger.debug('Removed organization parameter from URL');
          }
        }
      } catch (_) {
        /* ignore sessionStorage/URL access errors */
      }

      const authParams = {
        connection: 'Community-DB',
        audience: 'https://api.joinzealot.com',
        scope: 'openid profile email api:request offline_access',
        acr_values: 'http://schemas.openid.net/pape/policies/2007/06/multi-factor',
        brand: 'yubico',
        redirect_uri: typeof window !== 'undefined' ? window.location.origin : undefined,
        screen_hint: 'login',
        ...(promptLogin ? { prompt: 'login' } : {}),
      };

      await auth0LoginWithRedirect({
        authorizationParams: authParams,
        appState: {
          returnTo: isMobileViewport() ? '/Home' : '/HomeDesktop',
        },
      });
      return;
    }
  } catch (e) {
    logger.error(e, { context: 'AuthUtils' });
  }

  // If caller already has the connection name (e.g. from BrandContext), skip the legacy lookup
  let auth0ConnectionName = preloadedConnectionName || null;

  if (!auth0ConnectionName) {
    try {
      const response = await fetch(`${API_BASE_URL}/brand-config/sso-connection/${encodeURIComponent(appBrandName)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        let errorDetail = `API request failed with status ${response.status}`;
        try {
          const errorData = await response.json();
          errorDetail = errorData.message || errorData.error || JSON.stringify(errorData);
        } catch (e) {
          // Ignore if response is not JSON
        }
        const errorMessage = `Failed to fetch Auth0 connection name for brand '${appBrandName}' from backend. Detail: ${errorDetail}`;
        logger.error(new Error(errorMessage), { context: 'AuthUtils' });
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        if (data.length > 0 && data[0]?.ssoConnectionName) {
          auth0ConnectionName = data[0].ssoConnectionName;
        } else {
          const errorMessage = `Brand '${appBrandName}' does not have SSO configured. Falling back to SMS login.`;
          logger.info(errorMessage);
          const error = new Error(errorMessage);
          error.code = 'NO_SSO_CONFIGURED';
          throw error;
        }
      } else if (data && data.ssoConnectionName) {
        auth0ConnectionName = data.ssoConnectionName;
      } else {
        const errorMessage = `Brand '${appBrandName}' does not have SSO configured. Falling back to SMS login.`;
        logger.info(errorMessage);
        const error = new Error(errorMessage);
        error.code = 'NO_SSO_CONFIGURED';
        throw error;
      }

      if (!auth0ConnectionName) {
        const errorMessage = `Backend did not return a valid Auth0 connection name for brand: '${appBrandName}'. Response: ${JSON.stringify(data)}`;
        logger.error(new Error(errorMessage), { context: 'AuthUtils' });
        throw new Error(errorMessage);
      }
    } catch (error) {
      if (error.code === 'NO_SSO_CONFIGURED') {
        throw error;
      }

      const errorMessage = `Error fetching Auth0 connection name for brand '${appBrandName}': ${error.message}`;
      logger.error(error, { context: 'AuthUtils' });
      throw new Error(errorMessage);
    }
  }

  // const auth0ConnectionName = connectionMap[appBrandName]; // Use appBrandName to lookup in the map

  // if (!auth0ConnectionName) {
  //   const errorMessage = `No Auth0 connection configured for application brandName: '${appBrandName}'. Please check connectionMap in src/utils/authUtils.js. Available mapped brands: ${Object.keys(connectionMap).join(', ')}`;
  //   console.error('[AuthUtils]', errorMessage);
  //   // Also log the appBrandName that was attempted, for easier debugging.
  //   console.log(`[AuthUtils] Attempted appBrandName: ${appBrandName}`);
  //   throw new Error(errorMessage);
  // }

  try {
    logger.info('Attempting SSO login', { connection: auth0ConnectionName, brand: appBrandName });
    let promptLogin = false;
    try {
      if (typeof window !== 'undefined') {
        promptLogin = window.sessionStorage.getItem('force_interactive_login') === '1';
        // Clear the flag before redirect to avoid sticky state if the IdP errors
        if (promptLogin) window.sessionStorage.removeItem('force_interactive_login');
      }
    } catch (_) {
      /* intentionally ignore sessionStorage access errors */
    }

    // Remove organization parameter from URL if present (Auth0 SDK might read it from URL)
    if (typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('organization')) {
          url.searchParams.delete('organization');
          window.history.replaceState({}, '', url.toString());
          logger.debug('Removed organization parameter from URL');
        }
      } catch (_) {
        /* ignore URL access errors */
      }
    }

    // Build authorization params, explicitly excluding organization parameter
    const authParams = {
      connection: auth0ConnectionName, // Use the mapped, exact Auth0 connection name
      audience: 'https://api.joinzealot.com',
      scope: 'openid profile email api:request offline_access',
      redirect_uri: typeof window !== 'undefined' ? window.location.origin : undefined,
      ...(promptLogin ? { prompt: 'login' } : {}),
    };

    await auth0LoginWithRedirect({
      authorizationParams: authParams,
      appState: {
        returnTo: isMobileViewport() ? '/Home' : '/HomeDesktop',
      },
    });
  } catch (error) {
    logger.error(error, { connection: auth0ConnectionName, context: 'AuthUtils' });
    // Provide a friendly message upstream for brands without SSO configured
    const friendly = new Error(
      `Brand '${appBrandName}' does not have SSO configured. Falling back to email/password or one-time code.`,
    );
    friendly.code = 'NO_SSO_CONFIGURED';
    throw friendly;
  }
};
